import { ADMIN_ENDPOINTS } from './endpoints.ts';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../api/oauth/helpers.ts';
import type {
  User,
  UserOrganization,
  CreateUserRequest,
  ResetUserPasswordRequest,
  UpdateUserStatusRequest,
} from '../types.ts';

/** API returns flat array: [{ id, username, email, first_name, last_name, role, is_default }, ...] */
const mapMemberToUser = (item: Record<string, unknown>, organizationId?: string): User => ({
  id: item.id as number,
  username: (item.username as string) ?? '',
  email: (item.email as string) ?? '',
  first_name: (item.first_name as string) ?? '',
  last_name: (item.last_name as string) ?? '',
  is_active: item.is_active == null ? undefined : Boolean(item.is_active),
  role: (item.role as string) ?? undefined,
  organization_id: organizationId,
  is_default: Boolean(item.is_default),
});

const mapUserOrganizations = (raw: unknown): UserOrganization[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((item: Record<string, unknown>) => ({
    organization_id: String(item.organization_id ?? item.id ?? ''),
    organization_name: String(item.organization_name ?? item.name ?? ''),
    organization_slug: String(item.organization_slug ?? item.slug ?? ''),
    role: (item.role as string | undefined) ?? undefined,
    is_default: Boolean(item.is_default),
  }));
};

/** Pick display role: default org role, else first membership role. */
const roleFromOrganizations = (organizations: UserOrganization[]): string | undefined => {
  const defaultOrg = organizations.find((o) => o.is_default);
  if (defaultOrg?.role) return defaultOrg.role;
  return organizations.find((o) => o.role)?.role;
};

/** Get all users (admin). GET /api/admin/users — includes organizations[]. */
export const getAllUsers = (): Promise<{ data: User[] }> => {
  const url = ADMIN_ENDPOINTS.USERS;
  return apiGet(url).then((data: any) => {
    const raw = Array.isArray(data) ? data : data?.users ?? data?.data ?? [];
    const users: User[] = raw.map((item: Record<string, unknown>) => {
      const organizations = mapUserOrganizations(item.organizations);
      return {
        id: item.id as number,
        username: (item.username as string) ?? '',
        email: (item.email as string) ?? '',
        first_name: (item.first_name as string) ?? '',
        last_name: (item.last_name as string) ?? '',
        is_active: item.is_active == null ? undefined : Boolean(item.is_active),
        organizations,
        role: roleFromOrganizations(organizations),
      };
    });
    return { data: users };
  });
};

/** Get users for an organization via /api/admin/organizations/{organizationId}/members */
export const getUsersByOrganization = (
  organizationId: string
): Promise<{ data: User[] }> => {
  const url = ADMIN_ENDPOINTS.ORG_MEMBERS(organizationId);
  return apiGet(url).then((data: any) => {
    const raw = Array.isArray(data) ? data : data?.members ?? [];
    const users: User[] = raw.map((item: Record<string, unknown>) =>
      mapMemberToUser(item, organizationId)
    );
    return { data: users };
  });
};

export const getUserById = (id: number): Promise<{ data: User }> => {
  const url = ADMIN_ENDPOINTS.USER_BY_ID(id);
  return apiGet(url).then((data: any) => ({ data: (data.user ?? data) as User }));
};

export const getUserOrganizations = (
  id: number
): Promise<{ data: UserOrganization[] }> => {
  const url = ADMIN_ENDPOINTS.USER_ORGANIZATIONS(id);
  return apiGet(url).then((data: any) => {
    const raw = Array.isArray(data) ? data : data?.organizations ?? data?.data ?? [];
    const organizations: UserOrganization[] = raw.map(
      (item: Record<string, unknown>) => ({
        organization_id: String(item.organization_id ?? item.id ?? ''),
        organization_name: String(item.organization_name ?? item.name ?? ''),
        organization_slug: String(item.organization_slug ?? item.slug ?? ''),
        role: (item.role as string | undefined) ?? undefined,
        is_default: Boolean(item.is_default),
      })
    );
    return { data: organizations };
  });
};

export const createUser = (
  payload: CreateUserRequest
): Promise<{ data: User; message: string }> => {
  const url = ADMIN_ENDPOINTS.USERS;
  return apiPost(url, payload).then((data: any) => ({
    data: (data.user ?? data) as User,
    message: data.message ?? 'User created successfully',
  }));
};

export const updateUser = (
  id: number,
  payload: Partial<CreateUserRequest>
): Promise<{ data: User }> => {
  const url = ADMIN_ENDPOINTS.USER_BY_ID(id);
  return apiPatch(url, payload).then((data: any) => ({
    data: (data.user ?? data) as User,
  }));
};

export const resetUserPassword = (
  id: number,
  payload: ResetUserPasswordRequest
): Promise<{ message: string }> => {
  const url = ADMIN_ENDPOINTS.USER_PASSWORD(id);
  return apiPatch(url, payload).then((data: any) => ({
    message: data?.message ?? 'Password updated successfully',
  }));
};

export const updateUserStatus = (
  id: number,
  payload: UpdateUserStatusRequest
): Promise<{ data: User; message: string }> => {
  const url = ADMIN_ENDPOINTS.USER_STATUS(id);
  return apiPatch(url, payload).then((data: any) => ({
    data: (data.user ?? data) as User,
    message: data?.message ?? 'User status updated successfully',
  }));
};

/** Delete member from org via DELETE /api/admin/organizations/{orgId}/members/{memberId} */
export const deleteUser = (
  organizationId: string,
  memberId: number
): Promise<{ message: string }> => {
  const url = ADMIN_ENDPOINTS.ORG_MEMBER_BY_ID(organizationId, String(memberId));
  return apiDelete(url).then((data: any) => ({
    message: data?.message ?? 'User removed from organization',
  }));
};
